/**
 * Shared contract for BL Academy.
 *
 * Mirrors app/models/academy.py and app/schemas/academy.py — keep the three in
 * sync when adding a field, a status, or a stage.
 */
import type { Role } from "@/lib/roles";

export type AcademyStatus = "draft" | "published" | "archived";

export interface AcademyProgressState {
  is_saved: boolean;
  is_completed: boolean;
  completed_at: string | null;
  last_position_seconds: number;
  action_note: string | null;
  quiz_answer_index: number | null;
  /** When this viewer last touched the lesson. Orders the resume target. */
  updated_at: string | null;
}

/** One frame of the storyboard a lesson plays when it has no video. */
export interface AcademyStoryboardScene {
  /** Small caption above the headline, e.g. "THE QUESTION". */
  label: string;
  headline: string;
  body: string;
}

export interface AcademyLesson {
  id: string;
  title: string;
  summary: string | null;
  category: string | null;
  stage: string;
  stage_label: string;
  /** Admin routes only — the reader feed sends null and relies on the video id. */
  youtube_url: string | null;
  /** Null while a lesson is still being written. A PUBLISHED lesson always has
   *  one — the API refuses to publish without it — so reader surfaces can rely
   *  on it, and only the admin grid has to handle the empty case. */
  youtube_video_id: string | null;
  thumbnail_url: string | null;
  duration_seconds: number;
  /** The statement set large on the lesson tile. Falls back to `title`. */
  card_headline: string | null;
  /** One of CARD_TONES, or null to rotate by grid position. */
  card_tone: string | null;
  /** Played in place of a video when none is attached. Empty means neither. */
  storyboard: AcademyStoryboardScene[];
  key_takeaways: string[];
  transcript: string | null;
  action_prompt: string | null;
  quiz_question: string | null;
  quiz_answers: string[];
  /** Admin routes only. The reader learns the answer from the grade response —
   *  see submitQuizAnswer. */
  quiz_correct_index: number | null;
  quiz_explanation: string | null;
  display_order: number;
  status: AcademyStatus;
  published_at: string | null;
  is_active: boolean;
  /** roles.id values. Admin routes only. */
  role_ids: number[];
  /** Reader routes only. */
  progress: AcademyProgressState | null;
  created_at: string;
  updated_at: string;
}

/** The learning path as the server declares it — order and wording only. */
export interface AcademyStage {
  key: string;
  label: string;
  journey_title: string;
}

/** One stage with this viewer's numbers folded in. See stageProgress(). */
export interface AcademyStageProgress extends AcademyStage {
  lessonCount: number;
  completedCount: number;
}

export interface AcademyToolkitItem {
  id: string;
  title: string;
  description: string | null;
  lesson_id: string | null;
  lesson_title: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AcademyCaseMetric {
  label: string;
  value: string;
}

export interface AcademyCaseOption {
  label: string;
  feedback: string;
}

export interface AcademyCaseStudy {
  id: string;
  eyebrow: string;
  badge: string | null;
  headline: string;
  teaser: string | null;
  prompt: string | null;
  metrics: AcademyCaseMetric[];
  options: AcademyCaseOption[];
  lesson_id: string | null;
  lesson_title: string | null;
  display_order: number;
  status: AcademyStatus;
  published_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AcademyOverview {
  lessons: AcademyLesson[];
  stages: AcademyStage[];
  toolkit_items: AcademyToolkitItem[];
  case_study: AcademyCaseStudy | null;
}

export interface AcademyQuizResult {
  is_correct: boolean;
  correct_index: number;
  explanation: string | null;
}

export interface AcademyRoleOption {
  id: number;
  name: string;
  label: string;
}

export interface PaginatedAcademyLessons {
  items: AcademyLesson[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * Roles allowed to reach the Academy page.
 *
 * Mirrors PUBLISHABLE_ROLE_NAMES in app/models/academy.py plus admin (who
 * previews everything) and org_admin (whom the API grants read access to
 * everything published — see the org-admin branch of _caller_role_ids). Leaving
 * org_admin out would make RoleRoute bounce a tenant's admin off a page the
 * server is willing to serve them.
 */
export const ACADEMY_PAGE_ROLES: Role[] = ["buyer", "bl_advisor", "admin", "org_admin"];

/**
 * The three tile colourways the lesson grid alternates between. Mirrors
 * CARD_TONES in app/models/academy.py; each has a themed rule in index.css
 * (`.academy-tile-*`). A lesson with no tone set rotates through this list by
 * its position, so an unstyled run of lessons still alternates.
 */
export const CARD_TONES = ["cream", "ink", "slate"] as const;

export const CARD_TONE_LABELS: Record<string, string> = {
  cream: "Cream",
  ink: "Ink (dark)",
  slate: "Slate",
};

export const ACADEMY_STATUSES: AcademyStatus[] = ["draft", "published", "archived"];

export const ACADEMY_STATUS_LABELS: Record<AcademyStatus, string> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

/**
 * Stage keys are served by the API (GET /academy/overview and
 * /academy/admin/stages), so this map is icons ONLY — a stage added server-side
 * still appears, it just falls back to the generic icon until it gets an entry
 * here. Values are lucide icon names resolved by the pages that render them.
 */
export const STAGE_ICONS: Record<string, string> = {
  getting_started: "Compass",
  finding_a_business: "Search",
  understanding_the_numbers: "BarChart3",
  making_an_offer: "FileSignature",
  closing_and_ownership: "KeyRound",
};

/** The video's own thumbnail. Used whenever a lesson has no explicit override. */
export function youtubeThumbnail(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

/**
 * The embed URL for a lesson.
 *
 * youtube-nocookie.com rather than youtube.com: the player is embedded on an
 * authenticated page, and the privacy-enhanced host does not write tracking
 * cookies until playback starts. `rel=0` keeps the end screen to this channel
 * so a lesson doesn't hand the viewer someone else's video.
 */
export function youtubeEmbedUrl(
  videoId: string,
  opts: { start?: number; autoplay?: boolean } = {},
): string {
  const params = new URLSearchParams({
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
    // Required for the JS API to post progress messages back to the page.
    enablejsapi: "1",
    origin: window.location.origin,
  });
  if (opts.start && opts.start > 0) params.set("start", String(Math.floor(opts.start)));
  if (opts.autoplay) params.set("autoplay", "1");
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}

/** 150 -> "2:30". Zero renders as an em dash rather than a misleading "0:00". */
export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return "—";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    return `${hours}:${String(mins % 60).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

/** Total watch time for the momentum panel: "11 min" / "1 hr 20 min". */
export function formatInvested(seconds: number): string {
  if (!seconds || seconds <= 0) return "0 min";
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rest = mins % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
}

/** "2:30" or "1:05:00" back to seconds, for the admin duration field. */
export function parseDuration(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return 0;
  if (!/^\d{1,3}(:\d{1,2}){0,2}$/.test(trimmed)) return null;
  const parts = trimmed.split(":").map((p) => Number(p));
  if (parts.some((p) => Number.isNaN(p))) return null;
  // A bare number is minutes, which is how an author thinks about a lesson.
  if (parts.length === 1) return parts[0] * 60;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] * 3600 + parts[1] * 60 + parts[2];
}

/** The topic tabs on the reader page, built from the lessons actually present. */
export function lessonCategories(lessons: AcademyLesson[]): string[] {
  const seen = new Map<string, string>();
  for (const lesson of lessons) {
    const name = lesson.category?.trim();
    if (name && !seen.has(name.toLowerCase())) seen.set(name.toLowerCase(), name);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

export function formatPublishedDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// ── Derived state ────────────────────────────────────────────────────
// Everything below is computed from the lessons the reader already holds, each
// of which carries that viewer's own progress. The API deliberately does NOT
// also return these numbers: the page has to recompute them the moment someone
// saves or completes a lesson, and a server copy would be a second
// implementation of the same rules waiting to drift.

export interface AcademyMomentum {
  total: number;
  completed: number;
  saved: number;
  /** Seconds of lesson runtime the viewer has finished. */
  invested: number;
}

export function momentum(lessons: AcademyLesson[]): AcademyMomentum {
  let completed = 0;
  let saved = 0;
  let invested = 0;
  for (const lesson of lessons) {
    if (lesson.progress?.is_completed) {
      completed += 1;
      invested += lesson.duration_seconds;
    }
    if (lesson.progress?.is_saved) saved += 1;
  }
  return { total: lessons.length, completed, saved, invested };
}

/** The journey rail: each declared stage with this viewer's numbers folded in. */
export function stageProgress(
  stages: AcademyStage[],
  lessons: AcademyLesson[],
): AcademyStageProgress[] {
  return stages.map((stage) => {
    const inStage = lessons.filter((l) => l.stage === stage.key);
    return {
      ...stage,
      lessonCount: inStage.length,
      completedCount: inStage.filter((l) => l.progress?.is_completed).length,
    };
  });
}

/**
 * "You are here": the earliest stage that still has something unfinished.
 *
 * Stages holding no lessons are skipped — an empty stage is not somewhere
 * anyone is working. Once everything is done it settles on the last populated
 * stage rather than returning nothing, so the rail always marks a position.
 */
export function currentStage(stages: AcademyStageProgress[]): AcademyStageProgress | null {
  const working = stages.find((s) => s.lessonCount > 0 && s.completedCount < s.lessonCount);
  if (working) return working;
  return [...stages].reverse().find((s) => s.lessonCount > 0) ?? null;
}

/**
 * Where "Continue lesson" goes.
 *
 * A lesson genuinely left part-watched beats "the next one in order" — that is
 * what makes the hero read as picking up where you left off. Falls back to the
 * first unfinished lesson, and to null once everything is complete.
 */
export function continueLesson(lessons: AcademyLesson[]): AcademyLesson | null {
  const unfinished = lessons.filter((l) => !l.progress?.is_completed);
  // Most recently touched first — not curated order. Someone who dipped into
  // lesson 9 last night should be offered lesson 9, even though lesson 2 is
  // also part-watched and sorts earlier.
  const resumable = unfinished
    .filter((l) => (l.progress?.last_position_seconds ?? 0) > 0)
    .sort(
      (a, b) =>
        new Date(b.progress?.updated_at ?? 0).getTime() -
        new Date(a.progress?.updated_at ?? 0).getTime(),
    );
  return resumable[0] ?? unfinished[0] ?? null;
}

/** The toolkit's "Your saved actions" — every lesson carrying a written note. */
export function savedActions(
  lessons: AcademyLesson[],
): { lesson: AcademyLesson; note: string }[] {
  return lessons
    .filter((l) => !!l.progress?.action_note)
    .map((l) => ({ lesson: l, note: l.progress!.action_note! }));
}
